/**
 * The seven stall predicates (§5.8), rendered in plain language.
 *
 * They are read from `v_opportunity_effective` and never recomputed here —
 * that view is the single definition of all of it. There is no `alerts` table
 * in v1: nothing acknowledges or snoozes, so there is no state to materialize
 * and nothing here to dismiss.
 *
 * §9: never colour alone. Every badge carries its own words.
 */

import {
  AlertTriangle,
  Clock,
  PhoneOff,
  CalendarX,
  ListX,
  TrendingDown,
  Pause,
} from 'lucide-react';
import { STALL_PREDICATES, type StallPredicate } from '@menlo/shared';
import { Badge } from '@/components/ui/badge';
import { daysSince } from '@/lib/format';

type Row = Record<string, unknown>;

const ICONS: Record<StallPredicate, typeof Clock> = {
  stalled_stage_days: Clock,
  stalled_no_contact: PhoneOff,
  stalled_decision_date: CalendarX,
  stalled_next_step: ListX,
  stalled_overdue_action: AlertTriangle,
  stalled_win_not_ramping: TrendingDown,
  stalled_hold_review: Pause,
};

/** Short, for a table cell or a board card. */
const SHORT_LABELS: Record<StallPredicate, string> = {
  stalled_stage_days: 'Over stage target',
  stalled_no_contact: 'No contact 30d+',
  stalled_decision_date: 'Decision date passed',
  stalled_next_step: 'Next step overdue',
  stalled_overdue_action: 'Action overdue',
  stalled_win_not_ramping: 'Win not ramping',
  stalled_hold_review: 'Hold review due',
};

export function activeStalls(row: Row): StallPredicate[] {
  return STALL_PREDICATES.filter((predicate) => row[predicate] === true);
}

/**
 * The long form, for the detail rail's risk panel: "Won 14 months ago, ramp
 * date passed, no first order" rather than a predicate name.
 */
export function stallExplanation(predicate: StallPredicate, row: Row): string {
  switch (predicate) {
    case 'stalled_stage_days': {
      const days = row['days_in_current_stage'];
      const target = row['stage_target_days'];
      return `In ${String(row['stage_name'] ?? row['stage_code'])} for ${String(days)} days — the target is ${String(target)}.`;
    }
    case 'stalled_no_contact': {
      const last = row['last_customer_contact_at'] as string | null;
      return last === null || last === undefined
        ? 'No meeting with a customer attendee has ever been logged, and this deal is over 30 days old.'
        : `No customer contact for ${String(daysSince(last))} days. Partner-only meetings do not count.`;
    }
    case 'stalled_decision_date':
      return 'The expected decision date has passed and the deal is still open.';
    case 'stalled_next_step':
      return 'The next step is past its due date.';
    case 'stalled_overdue_action':
      return 'An action item on this deal is past due, at Design Evaluation or later.';
    case 'stalled_win_not_ramping': {
      const won = row['first_won_close_date'] as string | null;
      const days = daysSince(won ?? null);
      const months = days === null ? null : Math.round(days / 30);
      const when = months === null ? 'Won' : `Won ${months} months ago`;
      return `${when}, ramp date passed, no first order and no actual ramp date. This is the quiet failure mode of the business.`;
    }
    case 'stalled_hold_review':
      return 'The on-hold review date has passed. Someone said they would look at this again.';
    default:
      return SHORT_LABELS[predicate];
  }
}

export function RiskBadges({ row, max }: { row: Row; max?: number }): React.ReactElement | null {
  const stalls = activeStalls(row);
  if (stalls.length === 0) return null;
  const shown = max === undefined ? stalls : stalls.slice(0, max);
  const hidden = stalls.length - shown.length;

  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {shown.map((predicate) => {
        const Icon = ICONS[predicate];
        return (
          <Badge key={predicate} variant="risk" title={stallExplanation(predicate, row)}>
            <Icon className="h-3 w-3" aria-hidden="true" />
            {SHORT_LABELS[predicate]}
          </Badge>
        );
      })}
      {hidden > 0 && <Badge variant="outline">+{hidden}</Badge>}
    </span>
  );
}

/** The detail rail's risk panel: every active predicate, in a sentence each. */
export function RiskPanel({ row }: { row: Row }): React.ReactElement {
  const stalls = activeStalls(row);

  if (stalls.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        No stall signals. Seven predicates are evaluated continuously; nothing is firing.
      </p>
    );
  }

  return (
    <ul className="space-y-2">
      {stalls.map((predicate) => {
        const Icon = ICONS[predicate];
        return (
          <li key={predicate} className="flex items-start gap-2 text-xs">
            <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden="true" />
            <span>
              <span className="font-medium">{SHORT_LABELS[predicate]}.</span>{' '}
              <span className="text-muted-foreground">{stallExplanation(predicate, row)}</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export { SHORT_LABELS as STALL_LABELS };
